<template>
  <!-- 1. horizontal-sticky-facet-group -->
  <div
    v-if="!isZeroSearchResults && collection?.length"
    class="horizontal-sticky-facet-group"
  />
  <div class="productgrid-h3"></div>
  <!-- 2. horizontal-facet-group -->
  <div
    v-if="!isZeroSearchResults && collection?.length"
    class="horizontal-facet-group"
  >
    <FilteringFacets
      :facets="filteringFacetsData"
      :sort-by="Sortby"
      :display-modes="displayModes"
      :show-facet-selected-values-list="true"
      :drawer-options="drawerOptions"
      :do-dynamic-facet-counter-update-slideout="true"
      :slideout-facets="slideoutFacets"
      :product-count="productCount"
      :total-facet-selected-count="totalFacetSelectedCount"
      :max-desktop-filters="maxDesktopFilters"
      :max-tablet-filters-number="maxTabletFilters"
      :my-sizes-data="mySizesData"
      :disable-delivery-and-fulfillment-facet="disableDeliveryAndFulfillmentFacet"
      :consumer-store="store"
      :page-type="pageTypeLiteral"
      :xapi-experiment-ids="experimentIds"
      :device-info="deviceInfo"
      :is-s-y-s="isSYS"
      :is-dynamic-filters="isDynamicFilters"
      :is-products-loading="isLoading"
      @selected-criteria="getSelectedFacetsHandler"
      @selected-options="selectedOptions"
      @save-my-sizes-toggle="saveMySizesToggle"
    />
    <div class="sort-by-row">
      <AltGridViewButtons
        class="alt-grid-view-container"
        @updateGridView="updateGridView"
      />
      <SortableGridSortBy
        :options="sortOptions?.options"
        :value="sortOptions?.selected?.value"
        :is-loading="isLoading"
        @sort="onSort($event)"
      />
    </div>
  </div>

  <!-- 3. pills-container (DeselectionPills) -->
  <template v-if="!isSYS || (!isZeroSearchResults && collection?.length)">
    <DeselectionPills />
  </template>

  <!-- 4. grid-x grid-margin-x grid-margin-y sortable-grid-items small-up-2 medium-up-4 large-up-4 -->
  <SortableGridItems
    :items="collection"
    :products-list="collection"
    :analytics="analytics"
    :brand="brand"
    :loading="isLoading"
    :loading-items="productsPerPageRef"
    :no-items="isLoaded && !collection?.length"
    :alt-grid-view-classes="altGridViewClasses"
  />

  <!-- Teleport target: the Slideout component uses <Teleport to="#teleported"> for the mobile/tablet drawer.
       The main app provides this via index.template.html; the embed must supply it here. -->
  <div id="teleported" />

  <!-- 5. component-numeric-pagination numeric-pagination-wrapper -->
  <template v-if="!isZeroSearchResults && collection?.length">
    <NumericPagination
      class="numeric-pagination-wrapper"
      :current-page="pageIndexRef"
      :current-page-initial="metaPageNumber || pageIndexRef"
      :total="model?.meta?.itemCount"
      @paginationPageNumber="numericPaginationComposable?.onPageNumberClick"
      @paginationPrevious="numericPaginationComposable?.onPreviousClick"
      @paginationNext="numericPaginationComposable?.onNextClick"
    />
  </template>
</template>

<script setup>
/**
 * Portable PLP row for embed use.
 * Renders only: horizontal-sticky-facet-group, horizontal-facet-group,
 * pills-container, sortable-grid-items grid, numeric-pagination-wrapper.
 * Expects Vuex store to be populated (e.g. by host app / server); does not change any existing src files.
 */
import { useStore } from 'vuex';
import {
  computed,
  defineAsyncComponent,
  inject,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { brand } from 'config';
import FilteringFacets from '@components/FilteringFacets/components/FilteringFacets.vue';
import useFacets from '@modules/facets/composables/useFacets';
import usePageType from '@modules/discovery/composables/usePageType';
import useDevice from '@modules/discovery/composables/useDevice';
import useMaxFiltersNumber from '@modules/sortable-grid/composables/useMaxFiltersNumber';
import useNumericPagination from '@modules/sortable-grid/composables/useNumericPagination';
import useAltGridView from '@modules/sortable-grid/composables/useAltGridView';
import checkingRouteForSysPage from '@modules/shop-your-store/composables/checkRouteForSysPage';
import useFilteringFacets from '@components/FilteringFacets/composables/useFacets';
import { useSortableGrid } from '@modules/sortable-grid/composables';
import SortableGridItems from '@modules/sortable-grid/components/SortableGridItems.vue';
import SortableGridSortBy from '@modules/sortable-grid/components/SortableGridSortBy.vue';
import useExperimentIds from '@composables/useExperimentIds';
import useDynamicFilters from '@modules/featureControl/composables/useDynamicFilters';

const DeselectionPills = defineAsyncComponent(() => import('@components/FilteringFacets/components/criteria/DeselectionPills.vue'));
const AltGridViewButtons = defineAsyncComponent(() => import('@modules/sortable-grid/components/AltGridViewButtons.vue'));
const NumericPagination = defineAsyncComponent(() => import('@modules/sortable-grid/components/NumericPagination/NumericPagination.vue'));

const store = useStore();
const router = useRouter();
const route = useRoute();
/** Category id from mount options so it's always available even if API responses omit it. */
const embedCategoryId = inject('embedCategoryId', null);

/** Ensure id (and CategoryID for unmodified src) is in route query. Run before nav completes so loadSortableGrid sees it. */
router.beforeEach((to, _from, next) => {
  const categoryID = store.getters.getAnalyticsData?.categoryID ?? embedCategoryId;
  if (categoryID != null && to.query.id !== String(categoryID)) {
    next({
      path: to.path,
      query: { ...to.query, id: String(categoryID), CategoryID: String(categoryID) },
    });
  } else {
    next();
  }
});

/** Sync category id on initial load (route may not have it yet). */
function syncCategoryIdToRoute() {
  const categoryID = store.getters.getAnalyticsData?.categoryID ?? embedCategoryId;
  if (categoryID != null && route.query.id !== String(categoryID)) {
    router.replace({
      path: route.path,
      query: { ...route.query, id: String(categoryID), CategoryID: String(categoryID) },
    });
  }
}

const isMounted = ref(true);
onMounted(() => {
  syncCategoryIdToRoute();
});
onUnmounted(() => {
  isMounted.value = false;
});

watch(
  () => store.getters.getAnalyticsData?.categoryID,
  () => { syncCategoryIdToRoute(); },
);

const { pageTypeLiteral, isZeroSearchResults } = usePageType();
const {
  collection, analytics, model, sortOptions, updateSearchUrl,
  loadSortableGrid,
  currentSortby, pagination, isLoading, isLoaded,
} = useSortableGrid();

/** When route changes (sort/pagination/filter), fetch new data — embed has no ProductList/onRouteChange. */
function buildPagePayload() {
  const categoryId = store.getters.getAnalyticsData?.categoryID ?? embedCategoryId;
  const payload = {
    route,
    ...route.query,
    customerExperiment: store.getters.getMeta?.context?.customerExperiment,
  };
  if (categoryId != null && categoryId !== '') {
    payload.id = String(categoryId);
    payload.CategoryID = String(categoryId);
    payload.categoryId = String(categoryId);
  }
  return payload;
}

watch(
  () => route.fullPath,
  async (fullPath) => {
    if (route.path === '/') return;
    await loadSortableGrid(buildPagePayload(), { isActive: () => isMounted.value });
  },
);

const isSYS = ref(false);
isSYS.value = !!checkingRouteForSysPage();
const { experimentIds } = useExperimentIds();
const metaPageNumber = computed(() => store?.getters?.getMeta?.context?.pageNumber ?? 1);
const deviceInfo = useDevice();
const { isDynamicFilters } = useDynamicFilters();

store.commit('SET_PAGINATION_REFRESH', pagination?.value);

const { openedDropdown } = useFilteringFacets();
const pageIndexInitial = ref(pagination?.value?.pageIndex);

watch(pagination, (value, oldValue) => {
  if (value?.pageIndex !== oldValue?.pageIndex) {
    pageIndexInitial.value = value.pageIndex;
  }
});

const pageIndexRef = computed(() => pageIndexInitial.value);
const allSelectedFacets = computed(() => store.getters.allSelectedCriteria);
const productsPerPageRef = ref(pagination?.value?.perPage);
const selectedFacetsSaved = ref({ allSelected: allSelectedFacets });

async function onSort(value) {
  pageIndexInitial.value = 1;
  updateSearchUrl([
    { key: 'Sortby', value },
    { key: 'Pageindex', value: 1 },
  ]);
}

const {
  filteringFacetsData,
  getSelectedFacets,
  selectedOptions,
  slideoutFacets,
  productCount,
  totalFacetSelectedCount,
  mySizesData,
  saveMySizesToggle,
  disableDeliveryAndFulfillmentFacet,
} = useFacets();

const { maxDesktopFilters, maxTabletFilters } = useMaxFiltersNumber();
const Sortby = computed(() => store.getters.getSortOptionsForFacets);
const displayModes = { mobile: 'slideout', desktop: 'slideout', tablet: 'slideout' };
const visualFilters = computed(() => store.getters['visualFilterStore/getSelectedVisualFilters']);
const drawerOptions = {
  hideFilterBy: true,
  priceSymbol: true,
  desktopRedesign: true,
  mobileRedesign: true,
  tabletPhoneMarginIncrement: true,
};

const getSelectedFacetsHandler = (selectedFacets, filtered, areVisualsSelected) => {
  selectedFacetsSaved.value = selectedFacets;
  getSelectedFacets(selectedFacets, filtered, areVisualsSelected);
};

const numericPaginationComposable = useNumericPagination({ currentSortby, pageIndexInitial });

watch(() => visualFilters.value, (val, oldVal) => {
  if (!val?.length && !oldVal?.length) return;
  const filtered = [];
  const filteringFacetsMap = new Map(
    filteringFacetsData.value.map((facet) => [facet.displayName.toUpperCase(), facet]),
  );
  visualFilters.value.forEach((visualFacet) => {
    const uppercaseDisplayName = visualFacet.displayName.toUpperCase();
    if (filteringFacetsMap.has(uppercaseDisplayName)) {
      visualFacet.selectedValues = [...new Set(visualFacet.selectedValues)];
      visualFacet.displayName = uppercaseDisplayName;
      filtered.push(visualFacet);
    }
  });
  getSelectedFacetsHandler(selectedFacetsSaved.value, filtered, true);
});

const { altGridViewEnabled, altGridViewClasses } = useAltGridView();
const updateGridView = (classes) => {
  altGridViewClasses.value = classes;
};
</script>

<style lang="scss" src="./embed-extra.scss"></style>
<style scoped>
.numeric-pagination-wrapper {
  padding-top: 2rem;
}
</style>
